import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { BrandLogo } from '../components/BrandLogo';
import { Button } from '../components/ui/Button';
import { Field, Input } from '../components/ui/Field';
import { useAuth } from '../lib/auth';

export function SignupPage() {
  const { login, signUp } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmedDisplayName = displayName.trim();

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!trimmedDisplayName || trimmedDisplayName.length > 100 || isStarting) return;
    setError(null);
    setIsStarting(true);
    try {
      await signUp(trimmedDisplayName);
    } catch {
      setError('We could not start account creation. Please try again.');
      setIsStarting(false);
    }
  }

  async function handleSignIn() {
    if (isStarting) return;
    setError(null);
    setIsStarting(true);
    try {
      await login();
    } catch {
      setError('We could not start sign in. Please try again.');
      setIsStarting(false);
    }
  }

  return (
    <main className="grid min-h-screen bg-bone text-ink lg:grid-cols-[minmax(0,1fr)_minmax(420px,0.72fr)]">
      <section className="flex min-h-[34vh] flex-col justify-between bg-black px-6 py-7 text-ivory sm:px-10 sm:py-10 lg:min-h-screen lg:px-14 lg:py-12">
        <Link to="/" aria-label="Storyly home" className="w-fit">
          <BrandLogo variant="wordmark" className="h-16 brightness-0 invert sm:h-20" />
        </Link>
        <div className="max-w-2xl py-12 lg:py-0">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-beige">Built for photographers</p>
          <h1 className="mt-5 text-4xl font-semibold leading-[1.05] tracking-[-0.04em] sm:text-6xl">
            Your stories deserve a name.
          </h1>
          <p className="mt-5 max-w-xl text-base leading-7 text-beige sm:text-lg">
            Tell us what your clients know you by. We’ll use it to set up your private Storyly workspace and your first public gallery address.
          </p>
        </div>
        <p className="hidden text-xs uppercase tracking-[0.15em] text-beige/70 lg:block">Proudly South African</p>
      </section>

      <section className="flex items-center justify-center px-5 py-10 sm:px-10 lg:px-14">
        <div className="w-full max-w-md">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-ink/55">Create your account</p>
          <h2 className="mt-3 text-3xl font-semibold tracking-[-0.035em]">Let’s set up your studio</h2>
          <p className="mt-3 text-sm leading-6 text-ink/70">
            You’ll choose your email and password securely on the next screen.
          </p>

          <form className="mt-8 grid gap-5" onSubmit={handleSubmit}>
            <Field label="Business or display name" hint="Photographers can share a name — Storyly keeps every public address unique.">
              <Input
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                maxLength={100}
                required
                autoComplete="organization"
                placeholder="Your studio or photographer name"
                autoFocus
              />
            </Field>
            {error ? <p role="alert" className="text-sm text-clay">{error}</p> : null}
            <Button type="submit" className="w-full" disabled={!trimmedDisplayName || trimmedDisplayName.length > 100 || isStarting}>
              {isStarting ? 'Opening secure signup…' : 'Continue to secure signup'}
            </Button>
          </form>

          <div className="mt-7 border-t border-ink/15 pt-6 text-center">
            <p className="text-sm text-ink/65">Already have a Storyly account?</p>
            <button
              type="button"
              className="mt-2 min-h-11 font-semibold underline underline-offset-4 disabled:opacity-50"
              onClick={() => void handleSignIn()}
              disabled={isStarting}
            >
              Sign in
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}
